import { Activity, BarChart3, GitPullRequest, Target, TrendingUp, Zap, Settings, Users, Database, Shield, Network } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { usePermissions } from '../../hooks/use-rbac';
import { useAnalytics } from '../../hooks/useAnalytics';

type View = 'overview' | 'measure' | 'optimize' | 'deploy' | 'prove-impact';

interface SidebarProps {
  currentView: View;
  onViewChange: (view: View) => void;
  user?: any;
}

const navItems = [
  { id: 'overview' as View, label: 'Overview', icon: Activity, path: '/dashboard/overview' },
  { id: 'measure' as View, label: 'Measure', icon: BarChart3, path: '/dashboard/measure' },
  { id: 'optimize' as View, label: 'Optimize', icon: Zap, path: '/dashboard/optimize' },
  { id: 'deploy' as View, label: 'Deploy', icon: GitPullRequest, path: '/dashboard/deploy' },
  { id: 'prove-impact' as View, label: 'Prove Impact', icon: TrendingUp, path: '/dashboard/prove-impact' },
];

export function Sidebar({ currentView, onViewChange }: SidebarProps) {
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const analytics = useAnalytics();

  const handleNavClick = (destination: string) => {
    analytics.navigation.sidebarClick(destination);
  };

  const canManageUsers = hasPermission('users:create') || hasPermission('users:read');
  const canManageSSO = hasPermission('organization:manage_integrations');
  const canManageSecurity = hasPermission('security:manage') || hasPermission('security:read');
  const canViewPerformance = hasPermission('system:view_system_metrics') || hasPermission('security:read');
  const canAccessLakeFS = hasPermission('lakefs:read_repositories');
  const canAccessTopology = hasPermission('topology:read') || hasPermission('lakefs:read_repositories');

  return (
    <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <Target className="size-8 text-blue-600" />
          <div>
            <h1 className="text-gray-900">Briefcase AI</h1>
            <p className="text-sm text-gray-500">Telemetry Dashboard</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-6">
        {/* Main Navigation */}
        <div>
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
            Analytics
          </h3>
          <ul className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || currentView === item.id;

              return (
                <li key={item.id}>
                  <Link
                    to={item.path}
                    onClick={() => {
                      onViewChange(item.id);
                      handleNavClick(item.path);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className={`size-5 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                    <span>{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Data Management Navigation */}
        {(canAccessLakeFS || canAccessTopology) && (
          <div>
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
              Data Management
            </h3>
            <ul className="space-y-1">
              {canAccessTopology && (
                <li>
                  <Link
                    to="/dashboard/topology"
                    onClick={() => handleNavClick('/dashboard/topology')}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                      location.pathname === '/dashboard/topology'
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Network className={`size-5 ${
                      location.pathname === '/dashboard/topology' ? 'text-blue-600' : 'text-gray-400'
                    }`} />
                    <span>Topology Optimizer</span>
                  </Link>
                </li>
              )}
              {canAccessLakeFS && (
                <li>
                  <Link
                    to="/dashboard/lakefs"
                    onClick={() => handleNavClick('/dashboard/lakefs')}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                      location.pathname === '/dashboard/lakefs'
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Database className={`size-5 ${
                      location.pathname === '/dashboard/lakefs' ? 'text-blue-600' : 'text-gray-400'
                    }`} />
                    <span>LakeFS</span>
                  </Link>
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Admin Navigation */}
        {(canManageUsers || canManageSSO || canManageSecurity || canViewPerformance) && (
          <div>
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
              Administration
            </h3>
            <ul className="space-y-1">
              {canManageUsers && (
                <li>
                  <Link
                    to="/dashboard/admin/users"
                    onClick={() => handleNavClick('/dashboard/admin/users')}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                      location.pathname === '/dashboard/admin/users'
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Users className={`size-5 ${
                      location.pathname === '/dashboard/admin/users' ? 'text-blue-600' : 'text-gray-400'
                    }`} />
                    <span>User Management</span>
                  </Link>
                </li>
              )}
              {canManageSSO && (
                <li>
                  <Link
                    to="/dashboard/admin/sso"
                    onClick={() => handleNavClick('/dashboard/admin/sso')}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                      location.pathname === '/dashboard/admin/sso'
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Shield className={`size-5 ${
                      location.pathname === '/dashboard/admin/sso' ? 'text-blue-600' : 'text-gray-400'
                    }`} />
                    <span>SSO Configuration</span>
                  </Link>
                </li>
              )}
              {canManageSecurity && (
                <li>
                  <Link
                    to="/dashboard/admin/security"
                    onClick={() => handleNavClick('/dashboard/admin/security')}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                      location.pathname === '/dashboard/admin/security'
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Shield className={`size-5 ${
                      location.pathname === '/dashboard/admin/security' ? 'text-blue-600' : 'text-gray-400'
                    }`} />
                    <span>Security Dashboard</span>
                  </Link>
                </li>
              )}
              {canViewPerformance && (
                <li>
                  <Link
                    to="/dashboard/admin/performance"
                    onClick={() => handleNavClick('/dashboard/admin/performance')}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                      location.pathname === '/dashboard/admin/performance'
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Zap className={`size-5 ${
                      location.pathname === '/dashboard/admin/performance' ? 'text-blue-600' : 'text-gray-400'
                    }`} />
                    <span>Performance</span>
                  </Link>
                </li>
              )}
            </ul>
          </div>
        )}
      </nav>

      <div className="p-4 border-t border-gray-200">
        <div className="px-4 py-2">
          <p className="text-xs text-gray-500">Active Teams</p>
          <p className="text-gray-900">847</p>
        </div>
      </div>
    </div>
  );
}
